import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Pause, Play, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface QuizOperation {
  id: string;
  status: string;
  total_modules: number;
  processed_modules: number;
  successful_modules: number;
  failed_modules: number;
  started_at: string;
  completed_at?: string;
  last_heartbeat?: string;
  ai_tokens_used?: number;
  estimated_cost_usd?: number;
  paused_at?: string;
  error_message?: string;
}

export const QuizGenerationMonitor = () => {
  const [operation, setOperation] = useState<QuizOperation | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastNotifiedProgress, setLastNotifiedProgress] = useState(0);

  // Fetch active operation on mount
  useEffect(() => {
    fetchActiveOperation();
  }, []);

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel('quiz-generation-updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'quiz_generation_operations'
      }, (payload) => {
        const newOp = payload.new as QuizOperation;
        
        if (newOp.status === 'in_progress' || newOp.id === operation?.id) {
          setOperation(newOp);
          
          // Show toast every 25% progress
          const progress = Math.round((newOp.processed_modules / newOp.total_modules) * 100);
          if (progress % 25 === 0 && progress > lastNotifiedProgress && progress > 0) {
            toast.success(`🎯 ${progress}% complete - ${newOp.successful_modules} quizzes generated`, {
              duration: 5000
            });
            setLastNotifiedProgress(progress);
          }
          
          // Success notification
          if (newOp.status === 'completed' && operation?.status === 'in_progress') {
            toast.success(`🎉 All quizzes generated! ${newOp.successful_modules} successful, ${newOp.failed_modules} failed`, {
              duration: 10000
            });
          }
          
          // Error notification
          if (newOp.status === 'failed' && operation?.status === 'in_progress') {
            toast.error(`❌ Generation failed: ${newOp.error_message || 'Unknown error'}`, {
              duration: 10000
            });
          }
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [operation, lastNotifiedProgress]);

  const fetchActiveOperation = async () => {
    const { data } = await supabase
      .from('quiz_generation_operations')
      .select('*')
      .in('status', ['in_progress', 'paused'])
      .order('started_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (data) {
      setOperation(data as QuizOperation);
      const progress = Math.round((data.processed_modules / data.total_modules) * 100);
      setLastNotifiedProgress(Math.floor(progress / 25) * 25);
    }
  };

  const handlePause = async () => {
    if (!operation) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('quiz_generation_operations')
        .update({ 
          status: 'paused',
          paused_at: new Date().toISOString()
        })
        .eq('id', operation.id);

      if (error) throw error;
      
      toast.info('⏸️ Generation paused');
      await fetchActiveOperation();
    } catch (error) {
      console.error('Failed to pause:', error);
      toast.error('Failed to pause generation');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    if (!operation) return;
    
    setLoading(true);
    try {
      // Call autonomous generator to resume
      const { error } = await supabase.functions.invoke('autonomous-quiz-generator', {
        body: {
          resumeOperationId: operation.id
        }
      });

      if (error) throw error;
      
      toast.success('▶️ Generation resumed');
      await fetchActiveOperation();
    } catch (error) {
      console.error('Failed to resume:', error);
      toast.error('Failed to resume generation');
    } finally {
      setLoading(false);
    }
  };

  if (!operation) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-success" />
            Quiz Generation Status
          </CardTitle>
          <CardDescription>No active generation operations</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const progress = (operation.processed_modules / operation.total_modules) * 100;
  const statusConfig = {
    in_progress: { icon: Loader2, label: 'Generating', variant: 'default' as const, color: 'text-primary' },
    paused: { icon: Pause, label: 'Paused', variant: 'secondary' as const, color: 'text-warning' },
    completed: { icon: CheckCircle2, label: 'Complete', variant: 'success' as const, color: 'text-success' },
    failed: { icon: XCircle, label: 'Failed', variant: 'destructive' as const, color: 'text-destructive' }
  };

  const status = statusConfig[operation.status as keyof typeof statusConfig] || statusConfig.in_progress;
  const StatusIcon = status.icon;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusIcon className={`h-5 w-5 ${status.color} ${operation.status === 'in_progress' ? 'animate-spin' : ''}`} />
            <CardTitle>Quiz Generation Progress</CardTitle>
          </div>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
        <CardDescription>
          {operation.status === 'in_progress' && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Started {formatDistanceToNow(new Date(operation.started_at), { addSuffix: true })}
            </span>
          )}
          {operation.completed_at && (
            <span>Completed {formatDistanceToNow(new Date(operation.completed_at), { addSuffix: true })}</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Processed</p>
            <p className="text-2xl font-bold">{operation.processed_modules}/{operation.total_modules}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Successful</p>
            <p className="text-2xl font-bold text-success">{operation.successful_modules}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Failed</p>
            <p className="text-2xl font-bold text-destructive">{operation.failed_modules}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Success Rate</p>
            <p className="text-2xl font-bold">
              {operation.processed_modules > 0 
                ? Math.round((operation.successful_modules / operation.processed_modules) * 100)
                : 0}%
            </p>
          </div>
        </div>

        {/* Cost Tracking */}
        {(operation.ai_tokens_used || operation.estimated_cost_usd) && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground pt-2 border-t">
            {operation.ai_tokens_used && (
              <span>Tokens: {operation.ai_tokens_used.toLocaleString()}</span>
            )}
            {operation.estimated_cost_usd && (
              <span>Cost: ${operation.estimated_cost_usd.toFixed(4)}</span>
            )}
          </div>
        )}

        {/* Actions */}
        {operation.status === 'in_progress' && (
          <Button 
            onClick={handlePause} 
            disabled={loading}
            variant="outline"
            size="sm"
            className="w-full"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pause className="h-4 w-4 mr-2" />}
            Pause Generation
          </Button>
        )}

        {operation.status === 'paused' && (
          <Button 
            onClick={handleResume} 
            disabled={loading}
            variant="default"
            size="sm"
            className="w-full"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Resume Generation
          </Button>
        )}

        {/* Error Message */}
        {operation.error_message && (
          <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">Error</p>
              <p className="text-sm text-muted-foreground">{operation.error_message}</p>
            </div>
          </div>
        )}

        {/* Heartbeat Warning */}
        {operation.status === 'in_progress' && operation.last_heartbeat && (
          <StaleHeartbeatWarning lastHeartbeat={operation.last_heartbeat} />
        )}
      </CardContent>
    </Card>
  );
};

const StaleHeartbeatWarning = ({ lastHeartbeat }: { lastHeartbeat: string }) => {
  const minutesSinceHeartbeat = Math.floor((Date.now() - new Date(lastHeartbeat).getTime()) / 1000 / 60);
  
  if (minutesSinceHeartbeat < 10) return null;

  return (
    <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg">
      <AlertCircle className="h-4 w-4 text-warning mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-warning">Stale Operation Detected</p>
        <p className="text-sm text-muted-foreground">
          No heartbeat for {minutesSinceHeartbeat} minutes. Auto-recovery will trigger soon.
        </p>
      </div>
    </div>
  );
};
